import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useCurrentUser } from '@/hooks';
import {
  Avatar,
  Button,
  CircularProgress,
  Icon,
  IconButton,
  Paper,
  Typography,
} from '@/ui';
import { normalizeMemberBookingSlots } from '@/utils';

import {
  AMBIGUOUS_CHEMISTRY_BOOKING_MESSAGE,
  canonicalChemistryBookingCommand,
  clearChemistryBookingAttempt,
  clearConfirmedChemistryAttempt,
  loadChemistryBookingAttempt,
  saveChemistryBookingAttempt,
} from './bookingAttemptState';

import type {
  ChemistryBookingConfirmationCandidate,
  StoredChemistryBookingAttempt,
} from './bookingAttemptState';
import type {
  BrowserSchedulingContext,
  ParticipantAppointmentMutationResult,
  ParticipantAppointmentOperation,
  ParticipantSchedulingContext,
} from '@/features/appointments/types';
import type { MemberChemistryBookingSlot } from '@/types';

import { MentoCalDatePicker } from '@/components/MentoCal/DatePicker';
import { MentoCalTimeSlotButton } from '@/components/MentoCal/TimeSlotButton';
import { MENTO_LOGO_URL } from '@/components/MentoCal/constants';
import MemberBoundBooking from '@/features/appointments/MemberBoundBooking';
import PlatformAdminBookingPage from '@/features/appointments/PlatformAdminBookingPage';
import {
  APPOINTMENT_CONFIRMATION_PAUSED_MESSAGE,
  appointmentPendingMessage,
  appointmentRejectionMessage,
  PENDING_APPOINTMENT_PANEL_TEST_ID,
} from '@/features/appointments/appointmentStatusCopy';
import {
  EXACT_APPOINTMENT_CONTRACT,
  PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
} from '@/features/appointments/browserCapability';
import { coachIdentityQueryArg } from '@/features/appointments/coachIdentity';
import { resolveMemberCoachBookingState } from '@/features/appointments/memberCoachBookingState';
import { useOnBehalfBookingTarget } from '@/features/appointments/onBehalfBookingTarget';
import { shouldReconcileParticipantAppointmentWrite } from '@/features/appointments/participantAppointmentError';
import { toLegacyMemberBookingContext } from '@/features/appointments/schedulingContextAdapter';
import { appointmentUpgradeGuardSpent } from '@/features/appointments/useAppointmentLifecycleGuard';
import { useAppointmentClock } from '@/features/appointments/useNativeAppointmentRows';
import {
  appointmentApi,
  isTrustedCalendarEventTitleStale,
  shouldReconcileAppointmentWrite,
  trustedAppointmentDeclineMessage,
  useBookAppointmentMutation,
  useGetAppointmentOperationQuery,
  useGetAppointmentSchedulingContextQuery,
  useGetAppointmentsQuery,
} from '@/store/api/appointmentApi';
import { useGetMomentsFeatureFlagForLegacySubjectQuery } from '@/store/api/momentsApi';
import {
  useBookMomentsParticipantAppointmentMutation,
  useGetMomentsParticipantAppointmentOperationQuery,
  useGetMomentsParticipantSchedulingContextQuery,
  useLazyGetMomentsParticipantAppointmentQuery,
  useParticipantAppointmentRouteDecision,
} from '@/store/api/momentsParticipantAppointmentApi';
import { useAppDispatch } from '@/store/hooks';

const MIN_VISIBLE_WEEKS = 1;
const MAX_VISIBLE_WEEKS = 3;
const CONFIRMATION_DETAIL_RETRY_MS = 5000;
const MAX_CONFIRMATION_DETAIL_RETRIES = 12;

const DIVIDER = '#E3E3E3';
const ADMIN_COACH_CONFLICT_MESSAGE =
  'You cannot book on behalf of this member while signed in as the selected coach. Select a different coach to continue.';
const CONFIRMATION_REVIEW_MESSAGE =
  'We could not safely match the confirmed operation to the submitted appointment. Please contact support before trying again.';
const BOOKING_ATTEMPT_CAPACITY_MESSAGE =
  'Finish an existing booking attempt before starting another one.';

const getViewerTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const bookingErrorMessage = (error: unknown) => {
  // A trusted decline forwards an internal machine token in `error`
  // (calendar_conflict_check_unavailable, ...). The passthrough below would
  // render it verbatim, so declines are converted to canned copy here, at the
  // one funnel every booking failure on this page goes through. Unknown
  // reasons still resolve to safe copy, so a new upstream token cannot leak.
  const declined = trustedAppointmentDeclineMessage('booking', error);
  if (declined) return declined;

  if (error && typeof error === 'object' && 'data' in error) {
    const { data } = error as { data?: unknown };
    if (data && typeof data === 'object') {
      const message = (data as { error?: unknown }).error;
      if (typeof message === 'string' && message.trim()) return message;
    }
  }

  return 'That time is no longer available. Please choose another time.';
};

const slotStartTime = (slot: MemberChemistryBookingSlot) => slot.startTime;

const chemistryBookingRequest = (attempt: StoredChemistryBookingAttempt) =>
  attempt.bookContractVersion === 'appointment-book.v2' &&
  attempt.calendarEventTitle
    ? {
        ...attempt.command,
        browserContractMarker:
          attempt.browserContractMarker ?? EXACT_APPOINTMENT_CONTRACT,
        contractVersion: attempt.bookContractVersion,
        expectedCalendarEventTitle: attempt.calendarEventTitle,
      }
    : {
        ...attempt.command,
        browserContractMarker: PREVIOUS_EXACT_APPOINTMENT_CONTRACT,
      };

const participantBookingRequest = (attempt: StoredChemistryBookingAttempt) => ({
  bookingPattern: 'one_off' as const,
  counterpart: {
    externalId: attempt.command.coachId,
    provider: 'legacy_app',
  },
  idempotencyKey: attempt.command.idempotencyKey,
  kind: attempt.command.kind,
  startAt: attempt.command.startAt,
  viewerTimezone: attempt.command.viewerTimezone,
});

const participantRejection = (
  result:
    ParticipantAppointmentMutationResult | ParticipantAppointmentOperation,
) => {
  if (result.error) {
    return { code: result.error.error, message: result.error.error };
  }
  if (result.status === 'needs_reconciliation') {
    return {
      code: 'operation_needs_reconciliation',
      message: 'The appointment operation requires manual review.',
    };
  }
  return null;
};

const confirmationCandidateFromAppointment = (appointment: {
  bookingStatus: 'confirmed' | 'pending' | 'rejected';
  currentSchedule: { endAt: string; startAt: string };
  id: string;
  meeting: { joinUrl: string | null };
}): ChemistryBookingConfirmationCandidate => ({
  bookingId: appointment.id,
  endTime: appointment.currentSchedule.endAt,
  meetUrl: appointment.meeting.joinUrl,
  startTime: appointment.currentSchedule.startAt,
  status: appointment.bookingStatus,
});

const ownedConfirmationAppointment = (
  appointments: {
    bookingStatus: 'confirmed' | 'pending' | 'rejected';
    currentSchedule: { endAt: string; startAt: string; timezone?: string };
    expectedIdentities: {
      coach: { personId: string };
      member: { personId: string };
    };
    id: string;
    kind: string;
    meeting: { joinUrl: string | null };
    seatType: string | null;
    seriesId: string | null;
    seriesMutationVersion: number;
  }[],
  expected: {
    coachPersonId: string;
    memberPersonId: string;
    seatType: 'executive' | 'leadership' | null;
    startAt: string;
    timezone: string;
  },
  expectedStatus: 'confirmed' | 'pending' | 'rejected',
) =>
  appointments.find(
    (appointment) =>
      Date.parse(appointment.currentSchedule.startAt) ===
        Date.parse(expected.startAt) &&
      appointment.bookingStatus === expectedStatus &&
      appointment.currentSchedule.timezone === expected.timezone &&
      appointment.kind === 'chemistry' &&
      appointment.seriesId === null &&
      appointment.seriesMutationVersion === 0 &&
      appointment.seatType === expected.seatType &&
      appointment.expectedIdentities.coach.personId ===
        expected.coachPersonId &&
      appointment.expectedIdentities.member.personId ===
        expected.memberPersonId,
  );

const chemistryConfirmationOwnership = ({
  context,
  startAt,
}: {
  context: BrowserSchedulingContext | ParticipantSchedulingContext;
  startAt: string;
}) => ({
  coachPersonId: context.coach.personId,
  memberPersonId: context.member.personId,
  seatType: context.seatType,
  startAt,
  timezone: context.timezone,
});

/* ---------- Confirmation screen ---------- */

const CHEMISTRY_CALL_FAQ_URL =
  'https://mentoteam.notion.site/Mento-Chemistry-Call-FAQ-02720c0978814c43bc2f0d2f1e77e21c';

const BookingConfirmation = ({
  booking,
  coachFirstName,
  coachLastName,
  coachName,
  coachAvatarUrl,
  memberEmail,
  providerPending = false,
  timezone,
  onDone,
}: {
  booking: ChemistryBookingConfirmationCandidate;
  coachFirstName: string;
  coachLastName: string;
  coachName?: string;
  coachAvatarUrl?: string;
  memberEmail?: string;
  // MEN-4915: true while the Google artifact is still settling. The booking is
  // durable, but no invite has been sent yet and the operation can still end in
  // needs_reconciliation, so the copy must not claim one was.
  providerPending?: boolean;
  timezone: string;
  onDone: () => void;
}) => {
  const start = new Date(booking.startTime);
  const end = new Date(booking.endTime);

  const fmt = new Intl.DateTimeFormat('en', {
    day: 'numeric',
    month: 'long',
    timeZone: timezone,
    weekday: 'long',
    year: 'numeric',
  });
  const dateParts = fmt.formatToParts(start);
  let afterWeekday = false;
  const dateLabel = dateParts
    .map((p) => {
      if (p.type === 'weekday') {
        afterWeekday = true;
        return p.value;
      }
      if (afterWeekday && p.type === 'literal') {
        afterWeekday = false;
        return ' ';
      }
      return p.value;
    })
    .join('');

  const strip = (s: string) => s.replace(/\s?(am|pm)/iu, '');
  const period = (s: string) => (s.match(/am|pm/iu)?.[0] ?? '').toLowerCase();

  const rawStart = start.toLocaleTimeString('en', {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    timeZone: timezone,
  });
  const rawEnd = end.toLocaleTimeString('en', {
    hour: 'numeric',
    hour12: true,
    minute: '2-digit',
    timeZone: timezone,
  });

  const samePeriod = period(rawStart) === period(rawEnd);
  const startTime = samePeriod
    ? strip(rawStart)
    : rawStart.replace(' ', '').toLowerCase();
  const endTime = rawEnd.replace(' ', '').toLowerCase();

  const tzAbbr = new Intl.DateTimeFormat('en', {
    timeZone: timezone,
    timeZoneName: 'short',
  })
    .formatToParts(start)
    .find((p) => p.type === 'timeZoneName')?.value;

  const dateTimeLabel = `${dateLabel} ${startTime}-${endTime}${tzAbbr ? ` ${tzAbbr}` : ''}`;

  return (
    <main className="relative flex min-h-screen items-center justify-center bg-sunny-100 px-4 py-8">
      <IconButton
        aria-label="Close"
        className="absolute right-4 top-4"
        onClick={onDone}
      >
        <Icon>close</Icon>
      </IconButton>

      <div className="flex flex-col items-center gap-6">
        <img alt="Mento" className="h-8" src={MENTO_LOGO_URL} />

        <Paper
          aria-label="Chemistry call booked"
          className="flex w-full max-w-[516px] flex-col gap-4 p-8"
          elevation={3}
          role="region"
          style={{ border: '1px solid #E3E3E3', borderRadius: '16px' }}
        >
          {/* Coach identity */}
          <div className="flex items-center gap-4">
            <Avatar
              firstName={coachFirstName}
              lastName={coachLastName}
              size="s"
              src={coachAvatarUrl ?? ''}
            />
            <Typography variant="subtitle1">{coachName}</Typography>
          </div>

          {/* Title */}
          <Typography variant="h5">Chemistry call booked!</Typography>

          {/* Email */}
          <div className="flex items-center gap-4">
            <Icon className="text-xl" fontSize="small">
              mail
            </Icon>
            <Typography className="text-base font-medium text-cement-500">
              {providerPending
                ? `Booked for ${memberEmail} — calendar setup still processing`
                : `Invite sent to ${memberEmail}`}
            </Typography>
          </div>

          {/* Date & time */}
          <div className="flex items-center gap-4">
            <Icon className="text-xl" fontSize="small">
              event
            </Icon>
            <Typography className="text-base font-medium text-cement-500">
              {dateTimeLabel}
            </Typography>
          </div>

          {/* Info */}
          <div className="flex gap-4">
            <div className="pt-[3px]">
              <Icon className="text-xl" fontSize="small">
                info_outlined
              </Icon>
            </div>
            <div className="flex flex-col">
              <Typography className="text-base font-medium text-cement-500">
                A casual chat to get to know each other, explore the
                coach&apos;s style and approach, and see if it&apos;s a good
                match.
              </Typography>
              <Typography className="mt-3 text-base font-medium text-cement-500">
                <a
                  className="font-bold text-sage-500 underline"
                  href={CHEMISTRY_CALL_FAQ_URL}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Learn more
                </a>{' '}
                about chemistry calls and the Mento matching process.
              </Typography>
            </div>
          </div>

          {/* Done button */}
          <div className="flex justify-end">
            <Button
              color="primary"
              style={{ borderRadius: '12px' }}
              variant="contained"
              onClick={onDone}
            >
              Done
            </Button>
          </div>
        </Paper>
      </div>
    </main>
  );
};

/* ---------- Main booking page ---------- */

const ParticipantChemistryBooking = () => {
  const { coachId = '' } = useParams();
  const memberId = undefined;
  const isAdminMode = Boolean(memberId);
  const returnPath = isAdminMode
    ? `/coach/manage/coaches/${coachId}/schedule`
    : '/m';
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, isUserLoading } = useCurrentUser();
  const effectiveMemberId = user.id;
  const ownerKey =
    coachId && effectiveMemberId && user.id
      ? `${coachId}:${effectiveMemberId}:${user.id}`
      : '';
  const hasAdminCoachConflict = isAdminMode && user.id === coachId;
  const activeOwnerKey = useRef(ownerKey);
  activeOwnerKey.current = ownerKey;
  const instanceActive = useRef(true);
  const [storedAttempt, setStoredAttempt] =
    useState<StoredChemistryBookingAttempt | null>(() =>
      ownerKey ? loadChemistryBookingAttempt(ownerKey) : null,
    );
  const hasRestorableConfirmation =
    storedAttempt?.status === 'confirmed' &&
    !storedAttempt.confirmationInvalidated;
  const submissionClaimed = useRef(
    storedAttempt?.status === 'submitting' ||
      storedAttempt?.status === 'pending' ||
      hasRestorableConfirmation,
  );
  const [visibleWeeks, setVisibleWeeks] = useState(MIN_VISIBLE_WEEKS);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] =
    useState<MemberChemistryBookingSlot | null>(null);
  const [isTimeMenuOpen, setIsTimeMenuOpen] = useState(false);
  const [bookingError, setBookingError] = useState(
    storedAttempt?.errorMessage ?? '',
  );
  const [requiresFreshSchedulingContext, setRequiresFreshSchedulingContext] =
    useState(false);
  const [rejectionDismissed, setRejectionDismissed] = useState(false);
  const [operationPollingStopped, setOperationPollingStopped] = useState(false);
  // MEN-4915 (PR #1664 review): set when the post-admission pending write
  // fails. Durable storage then holds only the pre-dispatch submitting receipt
  // with no operation id, so neither a reload of this page nor Sessions/Upcoming
  // can adopt the operation. The member must be given the reference instead of
  // a confirmation they can navigate away from.
  const [pendingStorageUnavailable, setPendingStorageUnavailable] =
    useState(false);
  const [submissionUpgradeRequired, setSubmissionUpgradeRequired] =
    useState(false);
  const [pendingOperation, setPendingOperation] = useState<{
    operationId: string;
    retryAfterSeconds?: number | null;
    startAt: string;
  } | null>(() =>
    storedAttempt?.status === 'pending' && storedAttempt.operationId
      ? {
          operationId: storedAttempt.operationId,
          retryAfterSeconds: storedAttempt.retryAfterSeconds,
          startAt: storedAttempt.command.startAt,
        }
      : null,
  );
  const confirmationRecoveryOperation = useRef<string | null>(null);
  const confirmationDetailRetryCount = useRef(0);
  const confirmationDetailRetryTimer = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [confirmationDetailRetryTick, setConfirmationDetailRetryTick] =
    useState(0);
  const [confirmedBooking, setConfirmedBooking] =
    useState<ChemistryBookingConfirmationCandidate | null>(() =>
      hasRestorableConfirmation ? storedAttempt.confirmationCandidate : null,
    );
  const viewerTimezone = useMemo(getViewerTimezone, []);
  const releaseSubmissionClaim = () => {
    submissionClaimed.current = false;
  };

  useEffect(() => {
    instanceActive.current = true;

    return () => {
      instanceActive.current = false;
      if (confirmationDetailRetryTimer.current) {
        clearTimeout(confirmationDetailRetryTimer.current);
      }
    };
  }, []);

  const {
    currentData: coachIdentity,
    isFetching: isCoachIdentityFetching,
    isLoading: isCoachIdentityInitiallyLoading,
  } = useGetMomentsFeatureFlagForLegacySubjectQuery(
    coachIdentityQueryArg(coachId),
  );
  const isCoachIdentityLoading =
    isCoachIdentityInitiallyLoading || isCoachIdentityFetching;
  const routeDecision = useParticipantAppointmentRouteDecision({
    coachExternalId: coachId || undefined,
    establishedTransport:
      storedAttempt?.ownerKey === ownerKey
        ? (storedAttempt.transport ?? 'rails')
        : undefined,
    flow: 'booking',
  });
  const selectedTransport =
    routeDecision.state === 'ready' ? routeDecision.transport : undefined;

  // Route and authenticated member jointly own an in-flight booking attempt.
  useEffect(() => {
    if (confirmationDetailRetryTimer.current) {
      clearTimeout(confirmationDetailRetryTimer.current);
      confirmationDetailRetryTimer.current = null;
    }
    confirmationDetailRetryCount.current = 0;
    confirmationRecoveryOperation.current = null;
    setConfirmationDetailRetryTick(0);
    setVisibleWeeks(MIN_VISIBLE_WEEKS);
    setSelectedDate('');
    setSelectedSlot(null);
    setIsTimeMenuOpen(false);
    setBookingError('');
    setRequiresFreshSchedulingContext(false);
    setRejectionDismissed(false);
    setOperationPollingStopped(false);
    setSubmissionUpgradeRequired(false);
    submissionClaimed.current = false;
    if (!ownerKey) {
      setStoredAttempt(null);
      setPendingOperation(null);
      setConfirmedBooking(null);
      return;
    }
    const restored = loadChemistryBookingAttempt(ownerKey);
    setStoredAttempt(restored);
    if (restored?.status === 'pending' && restored.operationId) {
      submissionClaimed.current = true;
      setPendingOperation({
        operationId: restored.operationId,
        retryAfterSeconds: restored.retryAfterSeconds,
        startAt: restored.command.startAt,
      });
      // MEN-4915: a member returning while Google settles keeps their booking.
      setConfirmedBooking(restored.confirmationCandidate);
      return;
    }
    if (
      restored?.status === 'confirmed' &&
      !restored.confirmationInvalidated &&
      restored.confirmationCandidate
    ) {
      submissionClaimed.current = true;
      setPendingOperation(null);
      setConfirmedBooking(restored.confirmationCandidate);
      return;
    }
    if (restored?.status === 'submitting') submissionClaimed.current = true;
    setPendingOperation(null);
    setConfirmedBooking(null);
    setBookingError(restored?.errorMessage ?? '');
  }, [ownerKey]);

  const skipBookingDetailsQuery =
    isUserLoading || !coachId || selectedTransport !== 'rails';

  const {
    currentData,
    data: retainedData,
    error: schedulingContextError,
    isFetching,
    isLoading,
    refetch: refetchSchedulingContext,
  } = useGetAppointmentSchedulingContextQuery(
    { coachId, kind: 'chemistry', memberId, viewerTimezone, visibleWeeks },
    { skip: skipBookingDetailsQuery },
  );
  const momentsContextQuery = useGetMomentsParticipantSchedulingContextQuery(
    coachId && selectedTransport === 'moments'
      ? {
          counterpart: { externalId: coachId, provider: 'legacy_app' },
          kind: 'chemistry',
          viewerTimezone,
          visibleWeeks,
        }
      : skipToken,
  );
  const authoritativeCurrentData =
    selectedTransport === 'moments'
      ? momentsContextQuery.currentData
      : currentData;
  const authoritativeRetainedData =
    selectedTransport === 'moments' ? momentsContextQuery.data : retainedData;
  const refetchAuthoritativeSchedulingContext =
    selectedTransport === 'moments'
      ? momentsContextQuery.refetch
      : refetchSchedulingContext;
  const currentContext = authoritativeCurrentData
    ? toLegacyMemberBookingContext(authoritativeCurrentData)
    : undefined;
  const retainedContext = authoritativeRetainedData
    ? toLegacyMemberBookingContext(authoritativeRetainedData)
    : undefined;
  const data =
    currentContext ??
    (retainedContext?.coach.id === coachId &&
    retainedContext.member.id === user.id
      ? retainedContext
      : undefined);
  const now = useAppointmentClock(Boolean(user.id));
  const {
    data: appointmentPage,
    isError: isAppointmentsError,
    isFetching: isAppointmentsFetching,
    isSuccess: isAppointmentsSuccess,
  } = useGetAppointmentsQuery(user.id ? { userId: user.id } : skipToken, {
    refetchOnMountOrArgChange: true,
  });
  const routeOwnsStoredAttempt =
    !isUserLoading && storedAttempt?.ownerKey === ownerKey;
  const routeOwnsDurableAttempt =
    routeOwnsStoredAttempt &&
    (storedAttempt.status === 'submitting' ||
      storedAttempt.status === 'pending' ||
      storedAttempt.status === 'confirmed' ||
      storedAttempt.status === 'ambiguous' ||
      storedAttempt.status === 'review');
  const coachPersonId =
    coachIdentity?.subject.personId || authoritativeCurrentData?.coach.personId;
  const hasAuthoritativeAppointments =
    isAppointmentsSuccess && !isAppointmentsFetching;
  const bookingState = resolveMemberCoachBookingState({
    appointments: appointmentPage?.appointments ?? [],
    coachPersonId,
    hasAuthoritativeAppointments,
    isCoachIdentityFetching:
      isCoachIdentityLoading && !authoritativeCurrentData,
    legacyState: 'no_previous',
    now,
  });
  const hasActiveSeriesEligibility =
    authoritativeCurrentData?.eligibility.reasons.includes(
      'active_series_exists',
    ) ?? false;
  const isBookingStateUnavailable =
    isAppointmentsError || routeDecision.state === 'unavailable';
  const isBookingStateLoading =
    !routeOwnsDurableAttempt &&
    !isBookingStateUnavailable &&
    (routeDecision.state === 'loading' ||
      !hasAuthoritativeAppointments ||
      !coachPersonId);
  const existingBookingRedirect =
    bookingState === 'has_previous'
      ? `/m/book-biweekly/${coachId}`
      : returnPath;
  const shouldRedirectExistingBooking =
    !routeOwnsDurableAttempt &&
    !isBookingStateLoading &&
    (bookingState !== 'no_previous' || hasActiveSeriesEligibility);
  useEffect(() => {
    if (shouldRedirectExistingBooking) {
      navigate(existingBookingRedirect, { replace: true });
    }
  }, [existingBookingRedirect, navigate, shouldRedirectExistingBooking]);
  useEffect(() => {
    if (!data || selectedDate || selectedSlot) return;

    let earliest:
      | { date: string; slot: MemberChemistryBookingSlot; startsAt: number }
      | undefined;
    data.availability.days.forEach((day) => {
      if (!day.enabled) return;
      normalizeMemberBookingSlots(day.slots).forEach((slot) => {
        const startsAt = Date.parse(slotStartTime(slot));
        if (!Number.isFinite(startsAt)) return;
        if (!earliest || startsAt < earliest.startsAt) {
          earliest = { date: day.date, slot, startsAt };
        }
      });
    });
    if (!earliest) return;

    setSelectedDate(earliest.date);
    setSelectedSlot(earliest.slot);
  }, [data, selectedDate, selectedSlot]);
  const [bookAppointment, bookingResult] = useBookAppointmentMutation();
  const [bookMomentsAppointment, momentsBookingResult] =
    useBookMomentsParticipantAppointmentMutation();
  const [getMomentsAppointment] =
    useLazyGetMomentsParticipantAppointmentQuery();
  const scheduleConfirmationDetailRetry = () => {
    if (
      confirmationDetailRetryCount.current >= MAX_CONFIRMATION_DETAIL_RETRIES
    ) {
      if (!storedAttempt) return;
      const reviewAttempt: StoredChemistryBookingAttempt = {
        ...storedAttempt,
        errorMessage: CONFIRMATION_REVIEW_MESSAGE,
        status: 'review',
      };
      if (!saveChemistryBookingAttempt(reviewAttempt)) return;
      setStoredAttempt(reviewAttempt);
      setPendingOperation(null);
      setBookingError(CONFIRMATION_REVIEW_MESSAGE);
      return;
    }
    confirmationDetailRetryTimer.current = setTimeout(() => {
      confirmationDetailRetryTimer.current = null;
      confirmationDetailRetryCount.current += 1;
      confirmationRecoveryOperation.current = null;
      setConfirmationDetailRetryTick((tick) => tick + 1);
    }, CONFIRMATION_DETAIL_RETRY_MS);
  };
  const submitBookingAttempt = (attempt: StoredChemistryBookingAttempt) =>
    attempt.transport === 'moments'
      ? bookMomentsAppointment(participantBookingRequest(attempt))
      : bookAppointment(chemistryBookingRequest(attempt));
  const ownedPendingOperation =
    routeOwnsStoredAttempt &&
    storedAttempt.status === 'pending' &&
    storedAttempt.operationId === pendingOperation?.operationId
      ? pendingOperation
      : null;
  const operationQuery = useGetAppointmentOperationQuery(
    ownedPendingOperation && (storedAttempt?.transport ?? 'rails') === 'rails'
      ? {
          browserContractMarker: EXACT_APPOINTMENT_CONTRACT,
          operationId: ownedPendingOperation.operationId,
        }
      : skipToken,
    {
      pollingInterval:
        ownedPendingOperation && !operationPollingStopped
          ? Math.max(1, ownedPendingOperation.retryAfterSeconds ?? 5) * 1000
          : 0,
    },
  );
  const momentsOperationQuery =
    useGetMomentsParticipantAppointmentOperationQuery(
      ownedPendingOperation && storedAttempt?.transport === 'moments'
        ? { operationId: ownedPendingOperation.operationId }
        : skipToken,
      {
        pollingInterval:
          ownedPendingOperation && !operationPollingStopped
            ? Math.max(1, ownedPendingOperation.retryAfterSeconds ?? 5) * 1000
            : 0,
      },
    );
  const momentsOperationData = momentsOperationQuery.currentData
    ? {
        ...momentsOperationQuery.currentData,
        rejection: participantRejection(momentsOperationQuery.currentData),
      }
    : undefined;
  const operationData =
    storedAttempt?.transport === 'moments'
      ? momentsOperationData
      : operationQuery.currentData;
  const operationError =
    storedAttempt?.transport === 'moments'
      ? momentsOperationQuery.error
      : operationQuery.error;

  useEffect(() => {
    if (
      pendingOperation &&
      storedAttempt?.ownerKey === ownerKey &&
      storedAttempt.operationId === pendingOperation.operationId &&
      appointmentUpgradeGuardSpent(operationError)
    ) {
      setOperationPollingStopped(true);
    }
  }, [operationError, ownerKey, pendingOperation, storedAttempt]);

  useEffect(() => {
    if (
      storedAttempt?.status !== 'confirmed' ||
      storedAttempt.ownerKey !== ownerKey
    ) {
      return;
    }
    let invalidatedAttempt = storedAttempt;
    if (!storedAttempt.confirmationInvalidated) {
      invalidatedAttempt = {
        ...storedAttempt,
        confirmationInvalidated: true,
      };
      if (!saveChemistryBookingAttempt(invalidatedAttempt)) return;
      setStoredAttempt(invalidatedAttempt);
      dispatch(
        appointmentApi.util.invalidateTags([
          { id: 'SCHEDULING_CONTEXT', type: 'Appointment' },
          { id: 'LIST', type: 'Appointment' },
          'Session',
        ]),
      );
    }
    clearConfirmedChemistryAttempt({
      idempotencyKey: invalidatedAttempt.idempotencyKey,
      ownerKey: invalidatedAttempt.ownerKey,
    });
  }, [dispatch, ownerKey, storedAttempt]);

  useEffect(() => {
    if (
      !pendingOperation ||
      !operationData ||
      storedAttempt?.ownerKey !== ownerKey ||
      storedAttempt.operationId !== pendingOperation.operationId
    ) {
      return;
    }
    if (operationData.status === 'pending') {
      if (
        pendingOperation.retryAfterSeconds !== operationData.retryAfterSeconds
      ) {
        setPendingOperation({
          ...pendingOperation,
          retryAfterSeconds: operationData.retryAfterSeconds,
        });
        if (storedAttempt.status === 'pending') {
          const updatedAttempt = {
            ...storedAttempt,
            retryAfterSeconds: operationData.retryAfterSeconds ?? null,
          };
          saveChemistryBookingAttempt(updatedAttempt);
          setStoredAttempt(updatedAttempt);
        }
      }
      return;
    }
    if (operationData.status === 'confirmed') {
      const booking = storedAttempt.confirmationCandidate;
      const isSnapshotFreeDirectAttempt =
        storedAttempt.transport === 'moments' && booking === null;
      if (
        isSnapshotFreeDirectAttempt &&
        operationData.id === pendingOperation.operationId &&
        operationData.appointmentIds.length === 1
      ) {
        const confirmationContext = authoritativeCurrentData;
        if (!confirmationContext) return;
        if (
          confirmationRecoveryOperation.current === pendingOperation.operationId
        ) {
          return;
        }
        confirmationRecoveryOperation.current = pendingOperation.operationId;
        const detailRequest = getMomentsAppointment({
          appointmentId: operationData.appointmentIds[0]!,
        });
        detailRequest
          .unwrap()
          .then((appointment) => {
            if (
              !instanceActive.current ||
              activeOwnerKey.current !== ownerKey
            ) {
              return;
            }
            const confirmedAppointment = ownedConfirmationAppointment(
              [appointment],
              chemistryConfirmationOwnership({
                context: confirmationContext,
                startAt: pendingOperation.startAt,
              }),
              'confirmed',
            );
            if (!confirmedAppointment) {
              scheduleConfirmationDetailRetry();
              return;
            }
            const hydratedBooking =
              confirmationCandidateFromAppointment(confirmedAppointment);
            const confirmedAttempt: StoredChemistryBookingAttempt = {
              ...storedAttempt,
              confirmationCandidate: hydratedBooking,
              status: 'confirmed',
            };
            if (!saveChemistryBookingAttempt(confirmedAttempt)) {
              scheduleConfirmationDetailRetry();
              return;
            }
            confirmationDetailRetryCount.current = 0;
            confirmationRecoveryOperation.current = null;
            setStoredAttempt(confirmedAttempt);
            setConfirmedBooking(hydratedBooking);
            setPendingOperation(null);
          })
          .catch(() => {
            scheduleConfirmationDetailRetry();
          })
          .finally(() => detailRequest.unsubscribe());
        return;
      }
      if (
        !booking ||
        operationData.id !== pendingOperation.operationId ||
        operationData.appointmentIds.length !== 1 ||
        operationData.appointmentIds[0] !== booking.bookingId
      ) {
        const reviewAttempt: StoredChemistryBookingAttempt = {
          ...storedAttempt,
          errorMessage: CONFIRMATION_REVIEW_MESSAGE,
          status: 'review',
        };
        saveChemistryBookingAttempt(reviewAttempt);
        setStoredAttempt(reviewAttempt);
        setPendingOperation(null);
        return;
      }
      const confirmedAttempt: StoredChemistryBookingAttempt = {
        ...storedAttempt,
        confirmationCandidate: { ...booking, status: 'confirmed' },
        status: 'confirmed',
      };
      saveChemistryBookingAttempt(confirmedAttempt);
      setStoredAttempt(confirmedAttempt);
      setConfirmedBooking(confirmedAttempt.confirmationCandidate);
      setPendingOperation(null);
      return;
    }
    const rejectionMessage = appointmentRejectionMessage(
      operationData.rejection,
      'booking',
    );
    const rejectedAttempt: StoredChemistryBookingAttempt = {
      ...storedAttempt,
      confirmationCandidate: null,
      errorMessage: rejectionMessage,
      status: 'rejected',
    };
    saveChemistryBookingAttempt(rejectedAttempt);
    setStoredAttempt(rejectedAttempt);
    submissionClaimed.current = false;
    setBookingError(rejectionMessage);
    setPendingOperation(null);
  }, [
    confirmationDetailRetryTick,
    operationData,
    ownerKey,
    pendingOperation,
    storedAttempt,
  ]);

  const retryAmbiguousAttempt = async () => {
    if (
      submissionClaimed.current ||
      !authoritativeCurrentData ||
      storedAttempt?.status !== 'ambiguous' ||
      storedAttempt.ownerKey !== ownerKey
    ) {
      return;
    }
    const attemptOwnerKey = ownerKey;
    const submittingAttempt: StoredChemistryBookingAttempt = {
      ...storedAttempt,
      errorMessage: null,
      status: 'submitting',
    };
    submissionClaimed.current = true;
    saveChemistryBookingAttempt(submittingAttempt);
    setStoredAttempt(submittingAttempt);
    try {
      const response = await submitBookingAttempt(storedAttempt).unwrap();
      if (
        !instanceActive.current ||
        activeOwnerKey.current !== attemptOwnerKey
      ) {
        return;
      }
      if (response.status === 'pending') {
        const appointment = ownedConfirmationAppointment(
          response.appointments,
          chemistryConfirmationOwnership({
            context: authoritativeCurrentData,
            startAt: storedAttempt.command.startAt,
          }),
          'pending',
        );
        const pendingAttempt: StoredChemistryBookingAttempt = {
          ...submittingAttempt,
          confirmationCandidate: appointment
            ? confirmationCandidateFromAppointment(appointment)
            : null,
          operationId: response.operationId,
          retryAfterSeconds: response.retryAfterSeconds ?? null,
          status: 'pending',
        };
        setPendingStorageUnavailable(
          !saveChemistryBookingAttempt(pendingAttempt),
        );
        setStoredAttempt(pendingAttempt);
        setPendingOperation({
          operationId: response.operationId,
          retryAfterSeconds: response.retryAfterSeconds,
          startAt: storedAttempt.command.startAt,
        });
        // MEN-4915: confirm from the admission response. The booking is
        // durable here; only the Google calendar artifact is still settling,
        // and the confirmation renders nothing that comes from Google.
        setConfirmedBooking(pendingAttempt.confirmationCandidate);
        return;
      }
      if (response.status === 'confirmed') {
        const appointment = ownedConfirmationAppointment(
          response.appointments,
          chemistryConfirmationOwnership({
            context: authoritativeCurrentData,
            startAt: storedAttempt.command.startAt,
          }),
          'confirmed',
        );
        if (!response.operationId || !appointment) {
          const invalidAttempt: StoredChemistryBookingAttempt =
            response.operationId
              ? {
                  ...submittingAttempt,
                  confirmationCandidate: null,
                  errorMessage: CONFIRMATION_REVIEW_MESSAGE,
                  operationId: response.operationId,
                  status: 'review',
                }
              : {
                  ...submittingAttempt,
                  errorMessage: CONFIRMATION_REVIEW_MESSAGE,
                  status: 'error',
                };
          saveChemistryBookingAttempt(invalidAttempt);
          setStoredAttempt(invalidAttempt);
          releaseSubmissionClaim();
          setBookingError(CONFIRMATION_REVIEW_MESSAGE);
          return;
        }
        const booking = confirmationCandidateFromAppointment(appointment);
        const confirmedAttempt: StoredChemistryBookingAttempt = {
          ...submittingAttempt,
          confirmationCandidate: booking,
          operationId: response.operationId,
          status: 'confirmed',
        };
        saveChemistryBookingAttempt(confirmedAttempt);
        setStoredAttempt(confirmedAttempt);
        setConfirmedBooking(booking);
        return;
      }
      const rejectionMessage = appointmentRejectionMessage(
        'rejection' in response
          ? response.rejection
          : participantRejection(response),
        'booking',
      );
      const rejectedAttempt: StoredChemistryBookingAttempt = {
        ...submittingAttempt,
        errorMessage: rejectionMessage,
        operationId: response.operationId,
        status: 'rejected',
      };
      saveChemistryBookingAttempt(rejectedAttempt);
      setStoredAttempt(rejectedAttempt);
      releaseSubmissionClaim();
      setBookingError(rejectionMessage);
    } catch (retryError) {
      if (
        !instanceActive.current ||
        activeOwnerKey.current !== attemptOwnerKey
      ) {
        return;
      }
      const errorMessage = bookingErrorMessage(retryError);
      if (isTrustedCalendarEventTitleStale(retryError)) {
        setRequiresFreshSchedulingContext(true);
        clearChemistryBookingAttempt(attemptOwnerKey);
        setStoredAttempt(null);
        releaseSubmissionClaim();
        setSelectedSlot(null);
        setBookingError(errorMessage);
        try {
          await refetchAuthoritativeSchedulingContext().unwrap();
          if (activeOwnerKey.current === attemptOwnerKey) {
            setSelectedSlot(null);
            setRequiresFreshSchedulingContext(false);
          }
        } catch {
          // The trusted no-write result still requires a fresh selection.
        }
        return;
      }
      const upgradeGuardSpent = appointmentUpgradeGuardSpent(retryError);
      if (
        upgradeGuardSpent ||
        (storedAttempt.transport === 'moments'
          ? shouldReconcileParticipantAppointmentWrite(retryError)
          : shouldReconcileAppointmentWrite(retryError))
      ) {
        const ambiguousAttempt: StoredChemistryBookingAttempt = {
          ...submittingAttempt,
          errorMessage: AMBIGUOUS_CHEMISTRY_BOOKING_MESSAGE,
          status: 'ambiguous',
        };
        saveChemistryBookingAttempt(ambiguousAttempt);
        setStoredAttempt(ambiguousAttempt);
        releaseSubmissionClaim();
        if (upgradeGuardSpent) {
          setSubmissionUpgradeRequired(true);
        } else {
          setBookingError(errorMessage);
        }
        return;
      }
      const errorAttempt: StoredChemistryBookingAttempt = {
        ...submittingAttempt,
        errorMessage,
        status: 'error',
      };
      saveChemistryBookingAttempt(errorAttempt);
      setStoredAttempt(errorAttempt);
      releaseSubmissionClaim();
      setBookingError(errorMessage);
    }
  };

  const leaveDurableBooking = () => {
    navigate(returnPath);
  };

  const chooseAnotherTime = () => {
    releaseSubmissionClaim();
    setBookingError('');
    setPendingOperation(null);
    setSelectedSlot(null);
    setRejectionDismissed(true);
  };

  if (
    routeOwnsStoredAttempt &&
    submissionUpgradeRequired &&
    storedAttempt.status === 'ambiguous'
  ) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper className="max-w-md p-8 text-center" elevation={8}>
          <Typography className="mb-2" variant="h5">
            Booking submission is paused
          </Typography>
          <Typography className="text-cement-400">
            Refresh the page after updating the application, then retry this
            booking.
          </Typography>
          <Button
            className="mt-4"
            variant="outlined"
            onClick={leaveDurableBooking}
          >
            Close
          </Button>
        </Paper>
      </main>
    );
  }

  if (routeOwnsStoredAttempt && storedAttempt.status === 'ambiguous') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper className="max-w-md p-8 text-center" elevation={8}>
          <Typography className="mb-2" variant="h5">
            Booking status needs confirmation
          </Typography>
          <Typography className="mb-4 text-cement-400">
            {AMBIGUOUS_CHEMISTRY_BOOKING_MESSAGE} Retry only if you want to
            confirm the same booking request.
          </Typography>
          <div className="flex justify-center gap-3">
            <Button variant="outlined" onClick={leaveDurableBooking}>
              Close
            </Button>
            <Button
              disabled={!authoritativeCurrentData}
              variant="contained"
              onClick={retryAmbiguousAttempt}
            >
              Retry booking
            </Button>
          </div>
        </Paper>
      </main>
    );
  }

  if (routeOwnsStoredAttempt && storedAttempt.status === 'review') {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper className="max-w-md p-8 text-center" elevation={8}>
          <Typography className="mb-2" variant="h5">
            Booking confirmation needs review
          </Typography>
          <Typography className="text-cement-400">
            {storedAttempt.errorMessage}
          </Typography>
          <Button
            className="mt-4"
            variant="outlined"
            onClick={leaveDurableBooking}
          >
            Close
          </Button>
        </Paper>
      </main>
    );
  }

  if (ownedPendingOperation && operationPollingStopped) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper
          className="max-w-md p-8 text-center"
          elevation={8}
          style={{ borderRadius: 20 }}
        >
          <Typography className="mb-2" variant="h5">
            Booking confirmation is paused
          </Typography>
          <Typography className="text-cement-400">
            {APPOINTMENT_CONFIRMATION_PAUSED_MESSAGE}
          </Typography>
        </Paper>
      </main>
    );
  }

  // MEN-4915: checked before the pending panel below, which is retained but no
  // longer reached while a booking can be described -- it existed to cover the
  // Google-settling latency that used to make the wait visible. 'pending' is
  // admitted here; rejected and errored attempts carry neither status, so they
  // still reach their own handling rather than being masked by a confirmation.
  if (
    routeOwnsStoredAttempt &&
    (storedAttempt.status === 'confirmed' ||
      storedAttempt.status === 'pending') &&
    confirmedBooking &&
    // A pending attempt that was never persisted has no observer anywhere, so
    // it falls through to the pending panel below, which carries the reference.
    !(storedAttempt.status === 'pending' && pendingStorageUnavailable)
  ) {
    const [coachFirstName = '', ...coachLastNameParts] =
      storedAttempt.presentation.coachFullName.split(' ');
    return (
      <BookingConfirmation
        booking={confirmedBooking}
        coachAvatarUrl={storedAttempt.presentation.coachAvatarUrl ?? undefined}
        coachFirstName={coachFirstName}
        coachLastName={coachLastNameParts.join(' ')}
        coachName={storedAttempt.presentation.coachFullName}
        memberEmail={storedAttempt.presentation.memberEmail}
        providerPending={Boolean(ownedPendingOperation)}
        timezone={storedAttempt.presentation.timezone}
        onDone={() => {
          // MEN-4915 (PR #1664 review): this confirmation is now reachable
          // while the operation is still pending, so Done can fire before the
          // provider result is terminal. The stored receipt is the only thing
          // that lets observation resume -- nothing outside this page follows
          // chemistry attempts -- so clearing it while pending would strand the
          // operation and permanently hide a later rejection or
          // needs_reconciliation. Clear only once terminal, mirroring the
          // bi-weekly guard, where clearBookingAttempt's `expectedStatus`
          // defaults to 'confirmed' and skips a pending attempt.
          //
          // The other clearChemistryBookingAttempt call sites discard error and
          // stale-title attempts, where unconditional clearing is correct, so
          // the guard belongs here rather than in the storage helper.
          if (storedAttempt.status === 'confirmed') {
            clearChemistryBookingAttempt(ownerKey);
            setStoredAttempt(null);
          }
          // MEN-4915 (PR #1664 review): the pending-operation observer lives in
          // Sessions/Upcoming, but returnPath is '/m', whose index renders Home.
          // Leaving while the operation is still pending would strand it there,
          // losing the cache refresh and any later rejection or
          // needs_reconciliation. Send them to the observer instead.
          navigate(
            ownedPendingOperation && !isAdminMode
              ? '/m/sessions/upcoming'
              : returnPath,
          );
        }}
      />
    );
  }

  if (ownedPendingOperation) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper
          className="max-w-md p-8 text-center"
          data-testid={PENDING_APPOINTMENT_PANEL_TEST_ID}
          elevation={8}
          style={{ borderRadius: 20 }}
        >
          <Typography className="mb-2" variant="h5">
            Pending Appointment
          </Typography>
          <Typography className="text-cement-400">
            {appointmentPendingMessage('booking')}
          </Typography>
          {pendingStorageUnavailable ? (
            // MEN-4915: the pending receipt could not be persisted, so nothing
            // can adopt this operation later. Keep the member here with the
            // reference rather than offering a confirmation they can leave.
            <Typography className="text-cement-400">
              Keep this page open — we could not save this booking on this
              device. If you need help before it confirms, give support this
              reference: {ownedPendingOperation.operationId}
            </Typography>
          ) : null}
        </Paper>
      </main>
    );
  }

  if (
    routeOwnsStoredAttempt &&
    storedAttempt.status === 'rejected' &&
    storedAttempt.errorMessage &&
    !rejectionDismissed
  ) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper
          className="max-w-md p-8 text-center"
          elevation={8}
          style={{ borderRadius: 20 }}
        >
          <Typography className="mb-2" variant="h5">
            Booking not confirmed
          </Typography>
          <Typography className="text-cement-400">
            {storedAttempt.errorMessage}
          </Typography>
          <Button
            className="mt-4"
            variant="contained"
            onClick={chooseAnotherTime}
          >
            Choose another time
          </Button>
        </Paper>
      </main>
    );
  }

  if (
    isUserLoading ||
    isLoading ||
    momentsContextQuery.isLoading ||
    (isFetching && !data) ||
    (momentsContextQuery.isFetching && !data) ||
    routeDecision.state === 'loading' ||
    isBookingStateLoading ||
    shouldRedirectExistingBooking
  ) {
    return (
      <main className="relative flex min-h-screen flex-col items-center justify-center gap-16 bg-sunny-100 px-4 py-8">
        <img alt="Mento" className="h-8" src={MENTO_LOGO_URL} />
        <Paper
          aria-label="Loading booking details"
          className="flex min-h-[421px] w-full max-w-[1141px] items-center justify-center lg:h-[421px]"
          elevation={3}
          role="status"
          style={{ border: `1px solid ${DIVIDER}`, borderRadius: 16 }}
        >
          <div className="flex items-center gap-3">
            <CircularProgress size={20} />
            <Typography>Loading booking details...</Typography>
          </div>
        </Paper>
      </main>
    );
  }

  if (isBookingStateUnavailable || !data) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-sunny-200 px-4">
        <Paper
          className="max-w-md p-8 text-center"
          elevation={8}
          style={{ borderRadius: 20 }}
        >
          <Typography className="mb-2" variant="h5">
            We could not load this booking link.
          </Typography>
          <Typography className="text-cement-400">
            Make sure you are signed in with the member account assigned to this
            coach.
          </Typography>
        </Paper>
      </main>
    );
  }

  const [coachFirstName = '', ...coachLastNameParts] =
    data.coach.fullName?.split(' ') ?? [];
  const coachLastName = coachLastNameParts.join(' ');
  const selectedDay = data.availability.days.find(
    (day) => day.date === selectedDate,
  );
  const selectedDayDate = selectedDay?.date ?? '';

  // Validate selected slot still exists in current availability
  const slotStillValid =
    selectedSlot !== null &&
    selectedDay?.enabled === true &&
    selectedDay.slots.some(
      (s) => slotStartTime(s) === slotStartTime(selectedSlot),
    );
  const validatedSlot = slotStillValid ? selectedSlot : null;

  const selectedDateLabel = selectedDay
    ? new Date(`${selectedDay.date}T00:00:00`).toLocaleDateString('en', {
        day: 'numeric',
        month: 'short',
        weekday: 'short',
      })
    : 'Select a date';
  const canShowMore = visibleWeeks < MAX_VISIBLE_WEEKS;
  const calendarDays = data.availability.days.map((d) => ({
    date: d.date,
    enabled: d.enabled,
  }));
  // Prefer the booking-context member (the person being booked for — correct
  // when an EA/Ops books on behalf) and fall back to the logged-in member.
  const memberEmail = data.member.email ?? user.email;
  const availableSlots = normalizeMemberBookingSlots(
    selectedDay?.enabled ? selectedDay.slots : [],
  ).filter((slot) => Number.isFinite(Date.parse(slotStartTime(slot))));

  const onSelectDay = (date: string) => {
    if (storedAttempt?.status === 'error') {
      clearChemistryBookingAttempt(ownerKey);
      setStoredAttempt(null);
      submissionClaimed.current = false;
      setBookingError('');
    }
    setSelectedDate(date);
    setSelectedSlot(null);
    setIsTimeMenuOpen(false);
  };

  const onShowMore = () => {
    setVisibleWeeks((current) => Math.min(MAX_VISIBLE_WEEKS, current + 1));
  };

  const onSchedule = async () => {
    const startTime = validatedSlot ? slotStartTime(validatedSlot) : '';
    if (
      !startTime ||
      !authoritativeCurrentData ||
      schedulingContextError ||
      momentsContextQuery.error ||
      !selectedTransport
    )
      return;

    if (hasAdminCoachConflict) return;
    if (!ownerKey || submissionClaimed.current) {
      return;
    }

    const commandWithoutIdempotencyKey = {
      coachId,
      kind: 'chemistry' as const,
      startAt: startTime,
      viewerTimezone,
    };
    const canonicalCommand = canonicalChemistryBookingCommand(
      commandWithoutIdempotencyKey,
    );
    if (
      storedAttempt?.status === 'rejected' &&
      storedAttempt.canonicalCommand === canonicalCommand
    ) {
      return;
    }
    const idempotencyKey = window.crypto.randomUUID();
    const command = { ...commandWithoutIdempotencyKey, idempotencyKey };
    const submittedAttempt: StoredChemistryBookingAttempt = {
      ...(authoritativeCurrentData.contractVersion ===
      'appointment-scheduling-context.v2'
        ? {
            bookContractVersion: 'appointment-book.v2' as const,
            browserContractMarker: EXACT_APPOINTMENT_CONTRACT,
            calendarEventTitle: authoritativeCurrentData.calendarEventTitle,
          }
        : {}),
      canonicalCommand,
      command,
      confirmationCandidate: null,
      confirmationInvalidated: false,
      errorMessage: null,
      idempotencyKey,
      operationId: null,
      ownerKey,
      presentation: {
        coachAvatarUrl: data.coach.avatarUrl ?? null,
        coachFullName: data.coach.fullName ?? 'Coach',
        durationMinutes: data.durationMinutes,
        memberEmail,
        timezone: data.timezone,
      },
      retryAfterSeconds: null,
      status: 'submitting',
      transport: selectedTransport,
      version: 1,
    };
    const attemptOwnerKey = ownerKey;
    if (!saveChemistryBookingAttempt(submittedAttempt)) {
      setBookingError(BOOKING_ATTEMPT_CAPACITY_MESSAGE);
      return;
    }
    submissionClaimed.current = true;
    setStoredAttempt(submittedAttempt);

    try {
      setBookingError('');
      setPendingOperation(null);
      const response = await submitBookingAttempt(submittedAttempt).unwrap();
      if (
        !instanceActive.current ||
        activeOwnerKey.current !== attemptOwnerKey
      ) {
        return;
      }
      if (response.status === 'pending') {
        const appointment = ownedConfirmationAppointment(
          response.appointments,
          chemistryConfirmationOwnership({
            context: authoritativeCurrentData,
            startAt: command.startAt,
          }),
          'pending',
        );
        const pendingAttempt: StoredChemistryBookingAttempt = {
          ...submittedAttempt,
          confirmationCandidate: appointment
            ? confirmationCandidateFromAppointment(appointment)
            : null,
          operationId: response.operationId,
          retryAfterSeconds: response.retryAfterSeconds ?? null,
          status: 'pending',
        };
        setPendingStorageUnavailable(
          !saveChemistryBookingAttempt(pendingAttempt),
        );
        setStoredAttempt(pendingAttempt);
        setPendingOperation({
          operationId: response.operationId,
          retryAfterSeconds: response.retryAfterSeconds,
          startAt: startTime,
        });
        // MEN-4915: confirm from the admission response. The booking is
        // durable here; only the Google calendar artifact is still settling,
        // and the confirmation renders nothing that comes from Google.
        setConfirmedBooking(pendingAttempt.confirmationCandidate);
        return;
      }
      if (response.status === 'rejected') {
        const rejectionMessage = appointmentRejectionMessage(
          'rejection' in response
            ? response.rejection
            : participantRejection(response),
          'booking',
        );
        const rejectedAttempt: StoredChemistryBookingAttempt = {
          ...submittedAttempt,
          errorMessage: rejectionMessage,
          operationId: response.operationId,
          status: 'rejected',
        };
        saveChemistryBookingAttempt(rejectedAttempt);
        setStoredAttempt(rejectedAttempt);
        setRejectionDismissed(false);
        releaseSubmissionClaim();
        setBookingError(rejectionMessage);
        return;
      }
      const appointment = ownedConfirmationAppointment(
        response.appointments,
        chemistryConfirmationOwnership({
          context: authoritativeCurrentData,
          startAt: command.startAt,
        }),
        'confirmed',
      );
      if (!response.operationId || !appointment) {
        const invalidAttempt: StoredChemistryBookingAttempt =
          response.operationId
            ? {
                ...submittedAttempt,
                confirmationCandidate: null,
                errorMessage: CONFIRMATION_REVIEW_MESSAGE,
                operationId: response.operationId,
                status: 'review',
              }
            : {
                ...submittedAttempt,
                errorMessage: CONFIRMATION_REVIEW_MESSAGE,
                status: 'error',
              };
        saveChemistryBookingAttempt(invalidAttempt);
        setStoredAttempt(invalidAttempt);
        releaseSubmissionClaim();
        setBookingError(CONFIRMATION_REVIEW_MESSAGE);
        return;
      }
      const booking = confirmationCandidateFromAppointment(appointment);
      const confirmedAttempt: StoredChemistryBookingAttempt = {
        ...submittedAttempt,
        confirmationCandidate: booking,
        operationId: response.operationId,
        status: 'confirmed',
      };
      saveChemistryBookingAttempt(confirmedAttempt);
      setStoredAttempt(confirmedAttempt);
      setConfirmedBooking(booking);
    } catch (bookingRequestError) {
      if (
        !instanceActive.current ||
        activeOwnerKey.current !== attemptOwnerKey
      ) {
        return;
      }
      const errorMessage = bookingErrorMessage(bookingRequestError);
      if (isTrustedCalendarEventTitleStale(bookingRequestError)) {
        setRequiresFreshSchedulingContext(true);
        clearChemistryBookingAttempt(attemptOwnerKey);
        setStoredAttempt(null);
        releaseSubmissionClaim();
        setSelectedSlot(null);
        setBookingError(errorMessage);
        try {
          await refetchAuthoritativeSchedulingContext().unwrap();
          if (activeOwnerKey.current === attemptOwnerKey) {
            setSelectedSlot(null);
            setRequiresFreshSchedulingContext(false);
          }
        } catch {
          // The trusted no-write result still requires a fresh selection.
        }
        return;
      }
      const upgradeState = (
        bookingRequestError as { upgrade?: { state?: unknown } }
      ).upgrade?.state;
      if (
        upgradeState === 'reload-requested' ||
        upgradeState === 'guard-spent' ||
        (submittedAttempt.transport === 'moments'
          ? shouldReconcileParticipantAppointmentWrite(bookingRequestError)
          : shouldReconcileAppointmentWrite(bookingRequestError))
      ) {
        const ambiguousAttempt: StoredChemistryBookingAttempt = {
          ...submittedAttempt,
          errorMessage: AMBIGUOUS_CHEMISTRY_BOOKING_MESSAGE,
          status: 'ambiguous',
        };
        saveChemistryBookingAttempt(ambiguousAttempt);
        setStoredAttempt(ambiguousAttempt);
        releaseSubmissionClaim();
        if (upgradeState === 'guard-spent') {
          setSubmissionUpgradeRequired(true);
        }
        return;
      }
      const errorAttempt: StoredChemistryBookingAttempt = {
        ...submittedAttempt,
        errorMessage,
        status: 'error',
      };
      saveChemistryBookingAttempt(errorAttempt);
      setStoredAttempt(errorAttempt);
      releaseSubmissionClaim();
      setBookingError(errorMessage);
    }
  };

  return (
    <main className="relative flex min-h-screen flex-col items-center justify-start gap-16 bg-sunny-100 px-4 py-8 lg:pt-[123px]">
      <IconButton
        aria-label="Close"
        className="absolute right-4 top-4"
        onClick={() => {
          if (storedAttempt?.status === 'submitting') return;
          navigate(returnPath);
        }}
      >
        <Icon>close</Icon>
      </IconButton>
      <img alt="Mento" className="h-8" src={MENTO_LOGO_URL} />
      <Paper
        aria-label="Book a chemistry call"
        className="flex min-h-[421px] w-full max-w-[1141px] flex-col overflow-hidden lg:h-[421px] lg:flex-row"
        elevation={3}
        role="region"
        style={{ border: `1px solid ${DIVIDER}`, borderRadius: 16 }}
      >
        {/* -- Left sidebar: coach info -- */}
        <section
          aria-label="Chemistry call details"
          className="flex shrink-0 flex-col gap-4 border-b p-8 lg:h-full lg:min-h-0 lg:w-[379px] lg:overflow-y-auto lg:border-b-0 lg:border-r"
          style={{ borderColor: DIVIDER }}
        >
          <div className="flex items-center gap-3.5">
            <Avatar
              firstName={coachFirstName}
              lastName={coachLastName}
              size="s"
              src={data.coach.avatarUrl ?? ''}
            />
            <Typography className="text-base font-bold leading-normal">
              {data.coach.fullName}
            </Typography>
          </div>

          <div className="flex flex-col gap-2">
            <Typography
              className="text-2xl font-bold leading-tight"
              variant="h5"
            >
              Book a chemistry call
            </Typography>
            <Typography className="text-base leading-normal text-cement-400">
              A casual chat to get to know each other, explore the coach&apos;s
              style and approach, and see if it&apos;s a good match.
            </Typography>
          </div>

          <div className="flex flex-col gap-4 text-sm text-cement-400">
            {memberEmail && (
              <span className="flex items-center gap-1.5">
                <Icon className="text-base">person</Icon> {memberEmail}
              </span>
            )}
            <span className="flex items-center gap-1.5">
              <Icon className="text-base">videocam</Icon> Google Meet
            </span>
            <span className="flex items-center gap-1.5">
              <Icon className="text-base">schedule</Icon> {data.durationMinutes}{' '}
              Minutes
            </span>
            <span className="flex items-center gap-1.5">
              <Icon className="text-base">language</Icon> {data.timezone}
            </span>
          </div>
        </section>

        {/* -- Center: calendar grid -- */}
        <section
          aria-label="Choose a date"
          className="flex min-w-0 flex-1 flex-col border-b p-8 lg:h-full lg:min-h-0 lg:w-[407px] lg:flex-none lg:overflow-y-auto lg:border-b-0 lg:border-r"
          style={{ borderColor: DIVIDER }}
        >
          <MentoCalDatePicker
            canShowMore={canShowMore}
            days={calendarDays}
            isLoadingMore={isFetching}
            selectedDate={selectedDayDate}
            comfortable
            onSelectDate={onSelectDay}
            onShowMore={onShowMore}
          />
        </section>

        {/* -- Right: time slots + actions -- */}
        <section
          aria-label="Choose a time"
          className="flex w-full flex-col justify-between gap-4 p-8 lg:h-full lg:min-h-0 lg:w-[355px] lg:shrink-0 lg:overflow-y-auto"
        >
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <Typography className="text-base font-medium leading-[26px]">
              {selectedDateLabel}
            </Typography>

            <div className="relative flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
              {isAdminMode && selectedDay ? (
                <>
                  <button
                    aria-expanded={isTimeMenuOpen}
                    aria-haspopup="menu"
                    className="flex min-h-10 w-full items-center justify-center gap-2 bg-cement-100 px-3 py-2 text-[15px] font-bold"
                    type="button"
                    onClick={() => setIsTimeMenuOpen((open) => !open)}
                  >
                    {validatedSlot?.label ?? 'Select a time'}
                    <Icon>arrow_drop_down</Icon>
                  </button>
                  {isTimeMenuOpen ? (
                    <div
                      aria-label="Available times"
                      className="flex max-h-[240px] flex-col overflow-y-auto bg-cement-100"
                      role="menu"
                    >
                      {availableSlots.map((slot) => (
                        <button
                          key={slotStartTime(slot)}
                          className="min-h-8 w-full px-8 text-left text-[15px] font-bold hover:bg-cement-200"
                          role="menuitem"
                          type="button"
                          onClick={() => {
                            setSelectedSlot(slot);
                            setIsTimeMenuOpen(false);
                          }}
                        >
                          {slot.label}
                        </button>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : (
                availableSlots.map((slot) => {
                  const startTime = slotStartTime(slot);
                  const isActive =
                    validatedSlot !== null &&
                    slotStartTime(validatedSlot) === startTime;

                  return (
                    <MentoCalTimeSlotButton
                      key={startTime}
                      label={slot.label}
                      selected={isActive}
                      onClick={() => setSelectedSlot(slot)}
                    />
                  );
                })
              )}
              {selectedDay && availableSlots.length === 0 && (
                <Typography className="text-sm text-cement-400">
                  No times available.
                </Typography>
              )}
            </div>
          </div>

          {/* Actions: right-aligned, 20px gap */}
          <div>
            {(hasAdminCoachConflict || bookingError) && (
              <Typography className="mb-2 text-sm text-red-600">
                {hasAdminCoachConflict
                  ? ADMIN_COACH_CONFLICT_MESSAGE
                  : bookingError}
              </Typography>
            )}
            <div className="flex">
              <Button
                className="w-full rounded-xl bg-sage-500 px-5 py-2 text-[15px] font-bold tracking-[0.0267em] text-white shadow-none hover:bg-sage-500"
                disabled={
                  !validatedSlot ||
                  Boolean(schedulingContextError) ||
                  Boolean(momentsContextQuery.error) ||
                  !authoritativeCurrentData?.eligibility.eligible ||
                  bookingResult.isLoading ||
                  momentsBookingResult.isLoading ||
                  storedAttempt?.status === 'submitting' ||
                  requiresFreshSchedulingContext ||
                  hasAdminCoachConflict
                }
                size="small"
                variant="contained"
                onClick={onSchedule}
              >
                {bookingResult.isLoading || momentsBookingResult.isLoading
                  ? 'Booking...'
                  : 'Book'}
              </Button>
            </div>
          </div>
        </section>
      </Paper>
    </main>
  );
};

const MemberChemistryBooking = () => {
  const onBehalf = useOnBehalfBookingTarget();
  const navigate = useNavigate();
  if (onBehalf) {
    return (
      <PlatformAdminBookingPage
        bookingType="chemistry"
        coachId={onBehalf.coachId}
        kind="chemistry"
        memberId={onBehalf.memberId}
        onDone={() => navigate(onBehalf.returnPath)}
      />
    );
  }
  return (
    <MemberBoundBooking>
      <ParticipantChemistryBooking />
    </MemberBoundBooking>
  );
};

export default MemberChemistryBooking;
